import React, { useEffect, useRef } from 'react';
import { OpenSheetMusicDisplay } from 'opensheetmusicdisplay';

/**
 * Component to render notations in PrintView using OSMD
 * This is needed because script tags don't execute when inserted via dangerouslySetInnerHTML
 */
const PrintViewNotation = ({ notations }) => {
  const containerRefs = useRef([]);

  useEffect(() => {
    if (!notations || notations.length === 0) return;

    // Load and render each notation
    notations.forEach((notation, index) => {
      // Handle both string and object formats
      const notationContent = typeof notation === 'string' ? notation : notation?.content;
      if (!notationContent) return;
      
      const container = containerRefs.current[index];
      if (!container) return;

      // Clear any existing content
      container.innerHTML = '';

      try {
        // Initialize OSMD
        const osmd = new OpenSheetMusicDisplay(container, {
          backend: 'svg',
          drawTitle: false,  // Hide title from the XML
          drawComposer: false,  // Hide composer from the XML
          drawLyricist: false,  // Hide lyricist from the XML
          drawCredits: false,  // Hide credits from the XML
          drawPartNames: false,  // Hide part names from the XML
          autoResize: true,
          drawingParameters: 'compact',
          renderSingleHorizontalStaffline: false
        });

        // Handle base64 MXL files
        let contentToLoad = notationContent;
        if (typeof notationContent === 'string' && notationContent.startsWith('mxl:base64:')) {
          const base64Data = notationContent.substring('mxl:base64:'.length);
          // For MXL files, OSMD expects binary string
          contentToLoad = atob(base64Data);
        }

        // Load and render the notation
        osmd.load(contentToLoad).then(() => {
          osmd.render();
        }).catch((err) => {
          console.error(`Error loading notation ${index}:`, err);
          container.innerHTML = `<div style="color: #d00; padding: 20px;">Error loading notation: ${err.message}</div>`;
        });
      } catch (err) {
        console.error(`Error initializing OSMD for notation ${index}:`, err);
        container.innerHTML = '<div style="color: #d00; padding: 20px;">Error initializing notation viewer</div>';
      }
    });

    // Cleanup function
    return () => {
      containerRefs.current.forEach(container => {
        if (container) {
          container.innerHTML = '';
        }
      });
    };
  }, [notations]);

  if (!notations || notations.length === 0) {
    return null;
  }

  return (
    <div className="notation-section" style={{ marginTop: '40px', marginBottom: '20px' }}>
      <h2 style={{ fontSize: '18px', marginBottom: '15px', color: '#333' }}>Music Notation</h2>
      {notations.map((notation, index) => {
        const sectionRef = typeof notation === 'object' ? notation?.sectionRef : '';
        const title = typeof notation === 'object' ? notation?.title : '';
        const displayTitle = sectionRef && title ? `${sectionRef} - ${title}` : 
                            sectionRef || title || `Notation ${index + 1}`;
        
        return (
          <div key={index}>
            {(sectionRef || title) && (
              <h3 style={{ fontSize: '14px', marginBottom: '10px', color: '#555', fontWeight: '600' }}>
                {displayTitle}
              </h3>
            )}
            <div 
              ref={el => containerRefs.current[index] = el}
              style={{ 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                padding: '20px', 
                background: 'white', 
                minHeight: '200px', 
                marginBottom: '20px' 
              }}
            >
              <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                Loading {displayTitle}...
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PrintViewNotation;